// 横滑首尾内距由蓝图声明，放在滚动容器内。
import { contentPadding } from '../layout';
/**
 * L2 media — Carousel · MediaPicker · BeforeAfterCompare.
 *
 * `Carousel` used to have a twin — a paged slide track — and the registry
 * said so out loud, by declaring that twin as the stage's own `fallback`: "an
 * app without me renders the same slides, paged the same way, minus the
 * backdrop". A component whose graceful degradation is a near-copy of itself is
 * that component with two of its props turned off, so the twin merged in. The
 * stage carries everything it had: the `thumbs` filmstrip and the session
 * page's lead-photo prepend.
 *
 * The sealed rules came with it: the original is de-duplicated so it never
 * flashes twice, and a gallery ADD is append-not-replace with its own
 * per-photo placeholder.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  resetButton, ratioValue, longPressHandlers, toneColor, toneSoft,
  toSlides, sourceLadder,
} from './ui';
import { Image } from './primitives';
import { guardedInbox } from '../inbox';

// The COVER LADDER (`DEFAULT_SOURCES`) and its two readers — `toSlides` /
// `sourceLadder` — live in `ui.js` now, because the stage is no longer the only
// surface that has to answer "where is this row's picture": `ScanEditor` pages
// through the same list. Two spellings of that answer is how two media surfaces
// end up disagreeing about which field wins.

function arrowStyle(side) {
  return {
    ...resetButton,
    position: 'absolute', top: '50%', [side]: 8, transform: 'translateY(-50%)',
    width: 30, height: 30, borderRadius: 15, background: 'rgba(0,0,0,0.35)',
    color: '#fff', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
}

/**
 * SEALED: per-photo placeholder + APPEND-not-replace. `+` requests media from
 * the host (≤ max); each picked photo shows its own thumbnail + spinner
 * immediately and the page never navigates away.
 *
 * THE LOADING LANE (§6 image-placeholder-on-running). A `setItems` delivery
 * carrying `loading: true` is the picked photos an async run will turn into real
 * media (a session-continue append): they render APPENDED AFTER the base list,
 * each under a spinner, guarded (focus/until/seen) exactly like `NoticeList` —
 * `guardedInbox` is the ONE shared guard — so they vanish the frame the bound
 * feed grows past `seen`. A delivery with no `loading` is the legacy REPLACE:
 * its `items` become the base list, unchanged.
 *
 * THE FAILED LANE — and why the thumbs stay.
 *
 * When the host answers `dispatched: false` the work never started: the photos
 * the user picked are still theirs, and clearing the grid to a plain `+` reads
 * as "nothing happened", which is the one thing that is not true. So the picked
 * thumbs stay, under a red mark, with a line saying what to do — and `+` is
 * still there, because retrying IS re-picking. The failure arrives on the
 * `setStatus` slot (`{dispatched}` / `{failed}`) or as a bound `props.failed`,
 * so a blueprint can wire either the host's own answer or a derive.
 */
export function MediaPicker({ props = {}, data, ctx, emit, slots }) {
  // ⚠️ Read DECLARATIVELY — whatever is in the inbox is what this render shows,
  // including a payload delivered on a previous visit to the page (the slot
  // store outlives the page: `ViewIRPage` takes `pageId` as a prop and is never
  // remounted, and nothing dispatches the reducer's `reset`). The focus/until/
  // seen guard in `guardedInbox` is what keeps a stale delivery from bleeding
  // into the next row / staying past the real feed. `setStatus` is the live one
  // — medicine-box wires it, and a stale `{dispatched: false}` repaints the
  // failed lane over a visit where nothing failed. Same class as the
  // RoleMediaForm replay; see `useDelivery` there and the slot-lifecycle sweep.
  const inbox = guardedInbox(slots, ctx);
  const inboxLoading = inbox?.loading === true;
  const status = slots?.setStatus?.payload || null;
  const sources = props.sources;
  const slideLabel = props.slideLabel;
  const items = useMemo(() => {
    // slot (legacy REPLACE) → props → bind; a loading delivery does NOT replace
    // the base — its photos are the appended lane below.
    const raw = (inbox && !inboxLoading) ? inbox.items
      : (props.items !== undefined ? props.items : data);
    return toSlides(raw, { sources: sourceLadder(sources), labelField: slideLabel });
  }, [inbox, inboxLoading, props.items, sources, slideLabel, data]);
  const loadingItems = useMemo(() => (
    inboxLoading
      ? toSlides(inbox.items, { sources: sourceLadder(sources), labelField: slideLabel })
      : []
  ), [inboxLoading, inbox, sources, slideLabel]);
  const max = Number(props.max) || 4;
  const count = items.length + loadingItems.length;
  // `+` is shown whenever `add` is set — NOT gated on how many photos exist (the
  // twin always renders addPhoto, disabling it only mid-pick). `max` is only the
  // per-pick batch limit ridden on `addRequested` (clamped ≥1 below).
  const canAdd = props.add !== false;
  const failed = status?.dispatched === false || status?.failed === true || props.failed === true;
  const images = [...items.map((item) => item.src), ...loadingItems.map((item) => item.src)];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)', minWidth: 0 }}>
      {/* A horizontally-scrolling STRIP of fixed-width thumbs (the prod `.gallery`
          twin), never a wrapping grid — the strip scrolls when the photos run
          past one screen. */}
      <div style={{ display: 'flex', gap: 'var(--sp-2)', overflowX: 'auto', scrollbarWidth: 'none', minWidth: 0, ...contentPadding(props.contentPadding) }}>
        {items.map((s, i) => (
          <button
            key={s.src}
            type="button"
            onClick={() => emit?.('thumbActivated', {
              index: i, src: s.src, image_url: s.src, images,
            })}
            {...longPressHandlers(emit ? () => emit('longPressed', {
              index: i, src: s.src, image_url: s.src, images,
            }) : null)}
            style={{ ...resetButton, position: 'relative', flex: '0 0 auto', width: 84 }}
          >
            <Image props={{ src: s.src, fit: 'cover', ratio: '1/1' }} />
            {failed ? (
              <span
                role="img"
                aria-label="Not started"
                style={{
                  position: 'absolute', top: 4, right: 4,
                  width: 18, height: 18, borderRadius: 9,
                  background: toneColor('bad'), color: 'var(--surface)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 800, lineHeight: 1,
                }}
              >!</span>
            ) : null}
            {s.label ? (
              <span style={{
                position: 'absolute', left: 4, bottom: 4, maxWidth: 'calc(100% - 8px)',
                padding: '1px 6px', borderRadius: 'var(--r-pill)',
                background: 'rgba(0,0,0,0.45)', color: '#fff',
                fontSize: 'var(--t-xs)', fontWeight: 700,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{s.label}</span>
            ) : null}
          </button>
        ))}
        {/* the LOADING lane: the picked photos an async run has not turned into
            real media yet — the SAME thumb, under a dimming scrim and a spinner,
            appended after the base. The guard in `guardedInbox` drops the whole
            push the frame the bound feed grows, so these are REPLACED by the
            real rows rather than standing beside them. */}
        {loadingItems.map((s, i) => (
          <div key={`loading-${s.src}-${i}`} style={{ position: 'relative' }}>
            <Image props={{ src: s.src, fit: 'cover', ratio: '1/1' }} />
            <span aria-hidden style={{
              position: 'absolute', inset: 0, borderRadius: 'var(--r-sm)',
              background: 'rgba(0,0,0,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span role="img" aria-label="Loading photo" style={MEDIA_SPINNER} />
            </span>
          </div>
        ))}
        {canAdd ? (
          <button
            type="button"
            aria-label="Add photos"
            onClick={() => emit?.('addRequested', { max: Math.max(1, max - count) })}
            style={{
              ...resetButton,
              flex: '0 0 auto', width: 84,
              aspectRatio: '1 / 1', borderRadius: 'var(--r-sm)',
              border: failed ? `1px dashed ${toneColor('bad')}` : '1px dashed var(--line)',
              color: failed ? toneColor('bad') : 'var(--text-3)',
              background: failed ? toneSoft('bad') : undefined,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
            }}
          >+</button>
        ) : null}
      </div>
      {failed ? (
        <div role="alert" style={{ fontSize: 'var(--t-xs)', color: toneColor('bad'), lineHeight: 1.4 }}>
          {props.failedLabel || 'The update could not start. Tap + to try again.'}
        </div>
      ) : null}
    </div>
  );
}

// The in-flight ring drawn over an optimistically-added photo: a small
// indeterminate spinner in the token accent (same `viNativeSpin` keyframe the
// button spinner uses — `KitKeyframes` ships it once).
const MEDIA_SPINNER = {
  width: 18, height: 18, borderRadius: '50%',
  border: '2px solid color-mix(in srgb, var(--accent) 30%, transparent)',
  borderTopColor: 'var(--accent)',
  animation: 'viNativeSpin 0.8s linear infinite',
};

/**
 * Carousel — the HERO a detail page is built around: ONE subject, its slides.
 *
 * What it is, all of which the merged-in slide track had half of:
 *   · CONTAIN-fit over a blurred copy of the same image, so a portrait shot in
 *     a landscape stage fills its letterbox with itself instead of grey;
 *   · an OVERLAY region (`region: "overlay"` children) painted over the media —
 *     a badge, a play affordance, a scorebug — without escaping its box;
 *   · a selection that RETARGETS the rest of the page: `slideChanged {index,
 *     src}` is the signal the versions strip, the head and the actions all
 *     listen to, which is why the stage owns an index a connection can set;
 *   · synced dots, and an optional `thumbs` filmstrip under them;
 *   · on a SESSION-bound page (`ctx.focusGrid`) slide 0 is the ORIGINAL source
 *     photo and the derivatives from that capture follow. The dedup in
 *     `toSlides` does the rest of that locked rule for free: a derivative whose
 *     `image_url` is still the source photo — the host has not swapped the real
 *     one in yet — is skipped, so the original never flashes twice.
 *
 * A stage is NOT a collection: every slide is the same subject, so there is no
 * identity, no CRUD and no card template here. That is exactly the line the
 * taxonomy draws — a collection is named after what one CELL is, and a stage
 * has no cells.
 */
export function Carousel({ props = {}, data, ctx, emit, slots, childItems = [] }) {
  const lead = ctx?.focusGrid?.photo_url || null;
  const prioritySlides = useMemo(() => toSlides(props.prioritySlides, {
    sources: sourceLadder(props.sources),
    labelField: props.slideLabel,
  }), [props.prioritySlides, props.sources, props.slideLabel]);
  const slides = useMemo(() => {
    const source = props.slides !== undefined ? props.slides : data;
    const list = Array.isArray(source) ? source : (source ? [source] : []);
    const rest = toSlides(list, {
      sources: sourceLadder(props.sources),
      labelField: props.slideLabel,
    });
    // The LEAD is the capture's own source photo, and it is the one slide whose
    // caption the row cannot supply — it is not a row, so it does not go through
    // the cover ladder either. `leadLabel` names it ("Original"), which is
    // exactly the difference the user needs to see between slide 0 and
    // everything derived from it.
    // 新的优先结果排在旧列表前；旧蓝图未声明时仍保持原图领衔的兼容顺序。
    const merged = [
      ...prioritySlides,
      ...(lead ? [{ src: lead, label: props.leadLabel || '' }] : []),
      ...rest,
    ];
    const seen = new Set();
    return merged.filter((slide) => slide.src && !seen.has(slide.src) && seen.add(slide.src));
  }, [props.slides, props.sources, props.slideLabel, props.leadLabel, data, lead, prioritySlides]);
  const [index, setIndex] = useState(0);
  const [broken, setBroken] = useState(false);
  const [measuredRatio, setMeasuredRatio] = useState({ source: null, value: null });
  const goto = slots?.goto;

  useEffect(() => {
    const want = goto?.payload?.index;
    if (want != null) setIndex(Math.max(0, Math.min(slides.length - 1, Number(want) || 0)));
  }, [goto, slides.length]);
  useEffect(() => { if (index > slides.length - 1) setIndex(0); }, [slides.length, index]);

  const cur = slides[index] || null;
  useEffect(() => { setBroken(false); }, [cur?.src]);

  // Which slide (if any) to MEASURE for an intrinsic aspect (mirror Flutter
  // `buildViewIrCarousel`): `ratio:"source"` → the first generated slide;
  // NO `ratio` declared → the first displayed slide, so a landscape scan fits
  // instead of letterboxing into the 3/4 fallback (which now only holds until
  // the probe resolves); an explicit ratio → measure nothing, honor it fixed.
  const ratioSource = props.ratio === 'source'
    ? prioritySlides[0]?.src
    : (props.ratio == null ? slides[0]?.src : null);
  useEffect(() => {
    if (!ratioSource || /^media:\/\//i.test(String(ratioSource))) return undefined;
    // 第一张生成结果决定整组舞台比例；切换幻灯时不再重新测量或改变高度。
    const probe = new window.Image();
    let active = true;
    probe.onload = () => {
      if (active && probe.naturalWidth > 0 && probe.naturalHeight > 0) {
        setMeasuredRatio({ source: ratioSource, value: probe.naturalWidth / probe.naturalHeight });
      }
    };
    probe.src = ratioSource;
    return () => { active = false; };
  }, [ratioSource]);

  // A `when`-false child is HIDDEN, not absent (the interpreter marks it so the
  // slot graph keeps its shape) — an unfiltered list therefore painted one
  // full-bleed empty layer per gated overlay, each one eating the taps meant
  // for the picture underneath. Same filter Overlay/PageShell carry.
  const overlay = childItems.filter((c) => !c.hidden && (c.region === 'overlay' || !c.region));
  const sourceRatio = measuredRatio.source === ratioSource ? measuredRatio.value : null;
  const ratio = sourceRatio || ratioValue(props.ratio === 'source' ? null : props.ratio, '3 / 4');
  const images = slides.map((slide) => slide.src);

  const overlayLayer = overlay.length ? (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', padding: 'var(--sp-3)' }}>
      <div style={{ pointerEvents: 'auto', display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', minWidth: 0 }}>
        {overlay.map((c) => c.element)}
      </div>
    </div>
  ) : null;

  // NO SLIDES IS NOT NO STAGE. The overlay is a region of this component, not
  // of its media: a stage with a gated badge, a play affordance or a scorebug
  // over an empty slide list used to return the bare placeholder and LOSE all
  // of them, so a page whose hero had not resolved yet lost its affordances
  // too. The letterbox is the fallback for the PICTURE; the overlay still
  // draws over it.
  if (!cur) {
    return (
      <div style={{
        position: 'relative', width: `calc(100% - ${contentPadding(props.stagePadding).paddingLeft + contentPadding(props.stagePadding).paddingRight}px)`, marginLeft: contentPadding(props.stagePadding).paddingLeft, marginTop: contentPadding(props.stagePadding).paddingTop, marginBottom: contentPadding(props.stagePadding).paddingBottom, aspectRatio: ratio,
        borderRadius: 'var(--r)', overflow: 'hidden', background: 'var(--surface-3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontSize: 24,
      }}>
        ◍
        {overlayLayer}
      </div>
    );
  }

  // A ref the host has not re-signed yet is a `media://` HANDLE, not a URL, and
  // a raw <img> renders it as a broken frame — on the one surface a detail page
  // is built around, while the wall tile beside it (which goes through `Image`)
  // shows the piece's own monogram plate. Route both the pending and the broken
  // case through `Image` so the stage degrades exactly the way every other
  // picture in the kit does.
  const unresolved = /^media:\/\//i.test(String(cur.src || '')) || broken;
  const move = (delta) => {
    const next = (index + delta + slides.length) % slides.length;
    setIndex(next);
    emit?.('slideChanged', { index: next, src: slides[next].src });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)', minWidth: 0 }}>
      <div
        {...longPressHandlers(emit ? () => emit('longPressed', { index, src: cur.src }) : null)}
        style={{
          position: 'relative', width: `calc(100% - ${contentPadding(props.stagePadding).paddingLeft + contentPadding(props.stagePadding).paddingRight}px)`, marginLeft: contentPadding(props.stagePadding).paddingLeft, marginTop: contentPadding(props.stagePadding).paddingTop, marginBottom: contentPadding(props.stagePadding).paddingBottom, aspectRatio: ratio,
          maxHeight: props.maxHeight || undefined,
          borderRadius: 'var(--r)', overflow: 'hidden', background: 'var(--surface-3)',
          transition: 'aspect-ratio 180ms var(--ease)',
        }}
      >
        {/* the BACKDROP: the same picture, blurred and scaled, so the stage is
            never a grey letterbox around a portrait. Never drawn for a handle
            the host has not resolved — a blurred broken image is just noise. */}
        {/* cover 已铺满舞台；此时再画模糊副本没有视觉收益，还会增加切页叠层。 */}
        {props.backdrop === false || props.fit === 'cover' || unresolved ? null : (
          <img
            src={cur.src}
            alt=""
            aria-hidden
            style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              objectFit: 'cover', filter: 'blur(26px) saturate(1.3)', transform: 'scale(1.15)',
              opacity: 0.55,
            }}
          />
        )}
        {unresolved ? (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: '100%' }}>
              <Image props={{
                src: cur.src,
                ratio: props.ratio || '3/4',
                fit: props.fit === 'cover' ? 'cover' : 'contain',
                plate: false,
                rounded: false,
                alt: props.alt || cur.label || '',
                fallback: props.fallback,
                fallbackValue: props.fallbackValue ?? props.alt ?? cur.label,
                fallbackTone: props.fallbackTone,
              }} />
            </div>
          </div>
        ) : (
          <img
            src={cur.src}
            alt={props.alt || ''}
            onError={() => setBroken(true)}
            onClick={emit ? () => emit('imageTap', {
              src: cur.src, image_url: cur.src, index, images,
            }) : undefined}
            style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              objectFit: props.fit === 'cover' ? 'cover' : 'contain',
              cursor: emit ? 'zoom-in' : 'default',
            }}
          />
        )}
        {/* the slide's own name, over the picture. A stage that shows an
            original and three derivatives is unreadable without it: every
            slide is the same subject, so WHICH one you are looking at is the
            only thing the picture cannot say by itself. */}
        {cur.label ? (
          <span style={{
            position: 'absolute', top: 8, left: 8, maxWidth: 'calc(100% - 16px)',
            padding: '2px 8px', borderRadius: 'var(--r-pill)',
            background: 'rgba(0,0,0,0.45)', color: '#fff',
            fontSize: 'var(--t-xs)', fontWeight: 700,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{cur.label}</span>
        ) : null}
        {overlayLayer}
        {slides.length > 1 ? (
          <>
            <button type="button" aria-label="Previous" onClick={() => move(-1)} style={arrowStyle('left')}>‹</button>
            <button type="button" aria-label="Next" onClick={() => move(1)} style={arrowStyle('right')}>›</button>
          </>
        ) : null}
      </div>
      {props.dots !== false && slides.length > 1 ? (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
          {slides.map((s, i) => (
            <button
              key={s.src}
              type="button"
              aria-label={`Slide ${i + 1}`}
              onClick={() => { setIndex(i); emit?.('slideChanged', { index: i, src: s.src }); }}
              style={{
                ...resetButton, width: i === index ? 18 : 6, height: 6, borderRadius: 3,
                background: i === index ? 'var(--accent)' : 'var(--line)',
                transition: 'width 180ms var(--ease)',
              }}
            />
          ))}
        </div>
      ) : null}
      {/* the filmstrip the merged-in slide track carried: a second way to reach a
          slide, for a stage that holds more than a swipe's worth. */}
      {props.thumbs && slides.length > 1 ? (
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', scrollbarWidth: 'none', ...contentPadding(props.contentPadding) }}>
          {slides.map((s, i) => (
            <button
              key={`t-${s.src}`} type="button" aria-label={`Thumbnail ${i + 1}`}
              onClick={() => { setIndex(i); emit?.('slideChanged', { index: i, src: s.src }); }}
              style={{ ...resetButton, flex: '0 0 auto', width: 48 }}
            >
              <Image props={{ src: s.src, fit: 'cover', ratio: '1/1' }} />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * BeforeAfterCompare — two bound images and a reveal divider.
 *
 * Generic to the bone (`before` · `after` · `reveal`): it compares two
 * pictures and says how far the curtain is drawn. SEALED: dragging changes
 * only what is VISIBLE — it never writes, uploads or reorders anything — and
 * the divider is keyboard-operable (`role="slider"`, arrows / Home / End),
 * because a drag-only control is unusable for anyone who cannot drag.
 *
 * THE DRAG IS RELATIVE, and that is the shipped page's rule rather than a
 * preference: scene-studio's `template_detail.html` moves the curtain by the
 * pointer's DELTA from where it went down, and only once that delta passes
 * 4px. A press that jumps the curtain to the finger — what this drew before —
 * destroys the comparison with the tap that was meant to start it, and on a
 * phone every drag begins with exactly that tap. The threshold is the other
 * half: below it the gesture is still a scroll, and the page owns it.
 */
export function BeforeAfterCompare({ props = {}, data, emit, slots }) {
  const pair = Array.isArray(data) ? data : [];
  const before = props.before ?? pair[0];
  const after = props.after ?? pair[1];
  const setRevealSlot = slots?.setReveal;
  const [shown, setReveal] = useState(
    Number.isFinite(Number(props.reveal)) ? Number(props.reveal) : 50,
  );
  const [dragging, setDragging] = useState(false);
  const stage = useRef(null);
  // The gesture in flight: where it went down, the reveal it went down ON, and
  // the width that turns pixels into percent. A ref because a drag must not
  // re-render per move to keep its own origin.
  const drag = useRef(null);

  // A pushed position SEEDS the divider; dragging it afterwards must still
  // work, so the slot writes the state rather than shadowing it.
  //
  // ⚠️ THE PRE-MOUNT REPLAY (tracked follow-up, latent here). This effect also
  // runs on MOUNT, and the slot store outlives the page — `ViewIRPage` takes
  // `pageId` as a prop and is never remounted, and nothing dispatches the
  // reducer's `reset`. So a `setReveal` delivered on a PREVIOUS visit seeds the
  // divider of the next one. Latent only because no blueprint wires `setReveal`
  // today. `RoleMediaForm`'s `useDelivery` is the fix for the event-shaped
  // slots (mark a pre-mount delivery seen, never replay it); a position is
  // arguably state rather than an event, which is exactly the judgement the
  // follow-up has to make slot by slot — a blanket clear would also throw away
  // the applied filter that `slots.js` deliberately keeps across pages.
  useEffect(() => {
    if (setRevealSlot?.payload?.pct !== undefined) setReveal(Number(setRevealSlot.payload.pct) || 0);
  }, [setRevealSlot]);

  const put = (pct) => {
    const next = Math.max(0, Math.min(100, Math.round(pct)));
    setReveal(next);
    emit?.('positionChanged', { pct: next });
  };
  const onDown = (e) => {
    if (typeof e.button === 'number' && e.button !== 0) return;
    const box = stage.current?.getBoundingClientRect();
    drag.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startReveal: shown,
      width: Math.max(1, box?.width || 1),
      moved: false,
    };
    setDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onMove = (e) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const dx = e.clientX - d.startX;
    if (!d.moved && Math.abs(dx) < 4) return; // still a scroll, not a compare
    d.moved = true;
    put(d.startReveal + (dx / d.width) * 100);
  };
  const onUp = (e) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    drag.current = null;
    setDragging(false);
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };
  const onKey = (e) => {
    const step = { ArrowLeft: -2, ArrowDown: -2, ArrowRight: 2, ArrowUp: 2 }[e.key];
    if (step) { e.preventDefault(); put(shown + step); return; }
    if (e.key === 'Home') { e.preventDefault(); put(0); }
    if (e.key === 'End') { e.preventDefault(); put(100); }
  };

  if (!before && !after) return null;

  const picture = {
    position: 'absolute', inset: 0, width: '100%', height: '100%',
    display: 'block', objectFit: 'cover',
    pointerEvents: 'none', userSelect: 'none', WebkitUserDrag: 'none',
  };

  return (
    <div
      role="slider"
      tabIndex={0}
      aria-label={props.label || 'Compare before and after'}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={shown}
      aria-valuetext={`${shown}% before`}
      onKeyDown={onKey}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
      onLostPointerCapture={onUp}
      style={{
        position: 'relative', overflow: 'hidden',
        border: '1px solid var(--line)', borderRadius: 'var(--r-lg)',
        background: 'var(--surface-2)', boxShadow: 'var(--shadow-1)',
        outline: 'none',
      }}
    >
      <div
        ref={stage}
        style={{
          position: 'relative', width: '100%', overflow: 'hidden',
          aspectRatio: ratioValue(props.ratio, '1 / 1'),
          touchAction: 'pan-y', userSelect: 'none',
          cursor: dragging ? 'grabbing' : 'ew-resize',
        }}
      >
        <img src={after} alt={props.afterLabel || 'after'} draggable={false} style={picture} />
        <img
          src={before}
          alt={props.beforeLabel || 'before'}
          draggable={false}
          style={{ ...picture, clipPath: `inset(0 ${100 - shown}% 0 0)` }}
        />
        <span style={{ ...cornerTag, left: 12 }}>{props.beforeLabel || 'Before'}</span>
        <span style={{ ...cornerTag, right: 12 }}>{props.afterLabel || 'After'}</span>
        {/* the divider, and the grip that says it MOVES — a hairline alone
            reads as a seam between two pictures, not as a control */}
        <span aria-hidden style={{
          position: 'absolute', zIndex: 3, top: 0, bottom: 0, left: `${shown}%`, width: 2,
          background: 'rgba(255,255,255,0.92)', boxShadow: '0 0 0 1px rgba(0,0,0,0.12)',
          transform: 'translateX(-1px)', pointerEvents: 'none',
        }}>
          <span style={{
            position: 'absolute', top: '50%', left: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 38, height: 38, borderRadius: '50%',
            background: 'rgba(28,28,30,0.78)', color: '#fff',
            fontSize: 'var(--t-sm)', fontWeight: 800, letterSpacing: '-0.14em',
            transform: 'translate(-50%,-50%)', backdropFilter: 'blur(12px)',
          }}>‹›</span>
        </span>
      </div>
    </div>
  );
}

const cornerTag = {
  position: 'absolute', zIndex: 4, top: 12, padding: '0 9px',
  minHeight: 27, lineHeight: '27px', borderRadius: 'var(--r-pill)',
  background: 'rgba(20,20,22,0.58)', color: '#fff',
  fontSize: 'var(--t-xs)', fontWeight: 700, letterSpacing: '0.08em',
  textTransform: 'uppercase', backdropFilter: 'blur(10px)',
};
